import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { AddressInput } from "../../../clients/graphqlTypes.js";
import {
  SelectField,
  SelectOption,
} from "../../commons/form-fields/SelectField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import {
  FormikObserveState,
  FormikStateObserver,
} from "../../commons/FormikStateObserver.js";
import { AddressField } from "../addresses/AddressField.js";
import {
  useBuildingCreate__CreateBuildingMutation,
  useBuildingCreateModalQuery,
} from "./BuildingCreateForm.generated.js";
import { BuildingRow__BuildingFragment } from "./Buildings.generated.js";

interface FormValues {
  title: string;
  projectsiteAddress?: AddressInput;
  buildingTemplateId: string;
}

interface Props {
  crmOrganisationId?: string;
  handleComplete?: (
    building: BuildingRow__BuildingFragment,
    buildingRootItemId: string
  ) => void;
  refetchQueries?: string[];
  formId: string;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  hideTitle?: boolean;
  hideTemplate?: boolean;
}

export const BuildingCreateForm = ({
  crmOrganisationId,
  handleComplete,
  refetchQueries,
  formId,
  handleFormikObserveStateChange,
  hideTemplate,
  hideTitle,
}: Props) => {
  const { t } = useTranslate(["BuildingCreate", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [createBuilding] = useBuildingCreate__CreateBuildingMutation({
    client,
  });

  const onSubmit = async (values: FormValues) => {
    if (!values.projectsiteAddress) return;
    const res = await createBuilding({
      variables: {
        input: {
          title: values.title || values.projectsiteAddress!.streetLines1.trim(),
          crmOrganisationId,
          buildingAddress: {
            ...omit(values.projectsiteAddress, "__typename"),
            location: values.projectsiteAddress.location
              ? omit(values.projectsiteAddress.location, "__typename")
              : null,
          },
          templateQuoteId: values.buildingTemplateId || null,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
    });

    if (!res.data?.createBuilding.building) {
      throw new Error("Building creation failed");
    }

    enqueueSnackbar(
      t("Building created", {
        ns: "BuildingCreate",
      })
    );
    handleComplete?.(
      res.data!.createBuilding.building,
      res.data!.createBuilding.building.rootItem.id
    );
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string().label(t("Title", { ns: "BuildingCreate" })),
        projectsiteAddress: Yup.object()
          .label(t("Address", { ns: "BuildingCreate" }))
          .nullable()
          .required(t("Address is mandatory", { ns: "Global" })),
      }),
    [t]
  );

  return (
    <Formik<FormValues>
      initialValues={{
        title: "",
        buildingTemplateId: "",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form id={formId}>
        <Stack direction="column" spacing={2}>
          <AddressField
            required
            autoFocus
            name="projectsiteAddress"
            label={t("Address", {
              ns: "BuildingCreate",
            })}
            allowCustom={true}
          />
          <Stack spacing={1} direction="column">
            {!hideTitle && (
              <TextField
                name="title"
                label={
                  t("Title", {
                    ns: "BuildingCreate",
                  }) +
                  ` (${t("optional", {
                    ns: "Global",
                  })})`
                }
                type="text"
              />
            )}
            {!hideTemplate && <BuildingTemplateSelect />}
          </Stack>
        </Stack>
        <FormikStateObserver callback={handleFormikObserveStateChange} />
      </Form>
    </Formik>
  );
};

const BuildingTemplateSelect = () => {
  const { t } = useTranslate(["BuildingCreate"]);

  const client = useApolloClient();
  const query = useBuildingCreateModalQuery({
    client,
    variables: {},
  });
  const buildingTemplateOptions: SelectOption[] =
    getDataOrNull(query.data?.quoteTemplates)
      ?.edges.map(e => e.node)
      .map(e => ({
        value: e.id,
        label: e.title,
      })) ?? [];

  return (
    <SelectField
      disabled={!buildingTemplateOptions.length}
      name="buildingTemplateId"
      label={t("Building Template", {
        ns: "BuildingCreate",
      })}
      options={buildingTemplateOptions}
    />
  );
};
